import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { SectionHeading, Button } from '../components/ui';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './About.css';

// Import images using require() for Create React App asset processing
const heroImage = require('../assets/images/about/1925new.jpg');
const storyImage = require('../assets/images/about/1925new.jpg');

export function About() {
  useDocumentTitle('About');

  // Refs for scroll reveal sections
  const storyTextRef = useScrollReveal({ threshold: 0.1 });
  const storyImageRef = useScrollReveal({ threshold: 0.15 });
  const valuesRef = useScrollReveal({ threshold: 0.1 });
  const legacyRef = useScrollReveal({ threshold: 0.2 });

  return (
    <div className="about page-enter">
      {/* ──────────────────────────────────────────────────────────────
          HERO SECTION — Shorter, dramatic with dark overlay
          ────────────────────────────────────────────────────────────── */}
      <section className="hero-section hero-section--about">
        <div className="hero-section__background">
          <div
            className="hero-section__image"
            style={{
              backgroundImage: `url(${heroImage})`,
            }}
          />
          <div className="hero-section__overlay" />
        </div>

        <div className="hero-section__content">
          <h1 className="hero-section__title">How We Work</h1>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          STORY SECTION — Two-column layout (text left, image right)
          ────────────────────────────────────────────────────────────── */}
      <section className="story-section">
        <div className="story-section__container">
          {/* Text column */}
          <div className="story-section__text reveal" ref={storyTextRef}>
            <SectionHeading
              eyebrow="A steady process"
              heading="Good work starts with clear communication"
              align="left"
            />

            <div className="story-section__body">
              <p>
                Every project starts with a practical conversation. We listen closely, look at the existing conditions, and make sure the scope is understood before work begins.
              </p>

              <p>
                We work comfortably alongside homeowners, architects, designers, builders, and property teams. That means sharing information early, coordinating with the larger project, and being direct about what the work requires.
              </p>

              <p>
                Preparation is central to our work. It is where we address surface condition, protect the surrounding space, and establish the foundation for a finish that reads cleanly when the project is complete.
              </p>

              <p>
                We keep the jobsite orderly, communicate when conditions change, and stand behind the details that may not be obvious on day one. The result is work people feel comfortable recommending.
              </p>
            </div>
          </div>

          {/* Image column */}
          <div className="story-section__image reveal" ref={storyImageRef}>
            <img
              src={storyImage}
              alt="Mims Family Painting worksite photograph"
              className="story-section__image-element"
            />
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          VALUES SECTION — Dark background with architectural grid
          ────────────────────────────────────────────────────────────── */}
      <section className="values-section reveal" ref={valuesRef}>
        <div className="values-section__container">
          <div className="values-grid">
            {/* Preparation */}
            <div className="value-item">
              <div className="value-item__number">01</div>
              <h3 className="value-item__title">Preparation</h3>
              <p className="value-item__description">
                We address the condition beneath the finish, not just what is visible from across the room.
              </p>
            </div>

            {/* Coordination */}
            <div className="value-item">
              <div className="value-item__number">02</div>
              <h3 className="value-item__title">Coordination</h3>
              <p className="value-item__description">
                Clear schedules, clear expectations, and useful communication keep the work moving.
              </p>
            </div>

            {/* Care for the space */}
            <div className="value-item">
              <div className="value-item__number">03</div>
              <h3 className="value-item__title">Care for the Space</h3>
              <p className="value-item__description">
                We protect what is already in place and leave the jobsite in good order.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          LEGACY/CLOSING SECTION — Centered, inviting
          ────────────────────────────────────────────────────────────── */}
      <section className="legacy-section reveal" ref={legacyRef}>
        <div className="legacy-section__container">
          <div className="legacy-section__content">
            <h2 className="legacy-section__heading">
              A finish you can live with
            </h2>

            <p className="legacy-section__text">
              The best finish does not call attention to itself. It supports the room, wears well, and feels right long after the work is done.
            </p>

            <div className="legacy-section__cta">
              <Button
                asLink="/contact"
                variant="primary"
                size="lg"
              >
                Start a Conversation
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

About.displayName = 'About';
